import React, { FC, useCallback, useMemo, useState } from 'react';

import { reportEvent } from 'Shared/analytics';
import {
  GetDetailsResponseType,
  ThirdPartyReports,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import { useAppContext, useReportsContext } from 'QuoteApp/services/providers';

import { Report } from '../Report';
import { useReportRunner } from '../useReportRunner';
import { getReportStatusMultiple } from '../utils';
import { ReportStatus } from '../types';
import { SsnModal } from './SsnModal';

const hasSsnErrors = (drivers: GetDetailsResponseType['drivers']) => {
  return Object.keys(drivers).some(
    driverId => drivers[driverId]?.ssn?.errors?.length
  );
};

export const NcfReport: FC = () => {
  const { details } = useAppContext();
  const {
    acknowledgeDisclosure,
    changeReportInProgress,
    reportInProgress,
    reports,
    reportsDisabled,
  } = useReportsContext();
  const ncfReportCommand = reports['ncf'];
  const { runReport, messages } = useReportRunner('ncf');
  const [showSsnModal, changeShowSsnModal] = useState<boolean>(false);

  const ncfStatus = useMemo(() => {
    if (reportInProgress === ThirdPartyReports.Ncf) {
      return ReportStatus.Pending;
    }

    if (reports.driverDataEnhancement && ncfReportCommand) {
      return getReportStatusMultiple([
        reports.driverDataEnhancement.status,
        ncfReportCommand.status,
      ]);
    }

    return ReportStatus.Disabled;
  }, [ncfReportCommand, reports, reportInProgress]);

  const canRunReport = useMemo(() => {
    if (reportInProgress) {
      return false;
    }

    return (
      reports.driverDataEnhancement?.status === 'runnable' ||
      ncfReportCommand?.status === 'runnable' ||
      hasSsnErrors(details.drivers)
    );
  }, [details.drivers, reportInProgress, reports, ncfReportCommand?.status]);

  const openSsnModal = useCallback(() => {
    changeShowSsnModal(true);
    reportEvent('aqui-ssn-modal-displayed');
  }, []);

  const runReportOnClick = useCallback(
    async (currentDetails: GetDetailsResponseType) => {
      try {
        changeReportInProgress(ThirdPartyReports.Ncf);

        await acknowledgeDisclosure(Disclosures.Reports);

        if (hasSsnErrors(currentDetails.drivers)) {
          openSsnModal();
          return;
        }

        let currentNcfReport = currentDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.Ncf
        );

        const currentDriverDataEnhancementReport = currentDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.DriverDataEnhancement
        );

        if (currentDriverDataEnhancementReport?.status === 'runnable') {
          const newDetails = await runReport(
            currentDriverDataEnhancementReport,
            false
          );

          if (hasSsnErrors(newDetails.drivers)) {
            openSsnModal();
            return;
          }

          // No SSN Errors, check if NCF is runnable
          currentNcfReport = newDetails.commands.reports.find(
            r => r.reportName === ThirdPartyReports.Ncf
          );
        }

        if (currentNcfReport?.status !== 'runnable') {
          // DDE requires less info to be runnable; it is possible that not enough data is complete for NCF to be runnable
          changeReportInProgress(null);
          return;
        }
        await runReport(currentNcfReport, true);

        reportEvent('ncf-success');
        changeReportInProgress(null);
      } catch (e) {
        changeReportInProgress(null);
      }
    },
    [changeReportInProgress, acknowledgeDisclosure, openSsnModal, runReport]
  );

  return ncfReportCommand ? (
    <>
      <Report
        displayName="Credit-Based Insurance Score Report"
        messages={messages}
        enabled={!reportsDisabled && canRunReport}
        // ncf is the old name for cbis
        type="ncf"
        status={ncfStatus}
        onClick={() => {
          runReportOnClick(details);
        }}
      />
      <SsnModal
        open={showSsnModal}
        onCancel={() => {
          changeShowSsnModal(false);
          changeReportInProgress(null);
        }}
        onSubmit={newDetails => {
          changeShowSsnModal(false);
          runReportOnClick(newDetails);
        }}
      />
    </>
  ) : null;
};
